import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, X as XIcon } from 'lucide-react';
import { api } from '@/api/client';
import { PlanPicker } from './PlanPicker';

interface Courier { id: number; name: string; orderType: 'B2C' | 'B2B'; serviceType: string; }

interface SlabRow {
  upto?: string;
  city?: number;
  state?: number;
  metro?: number;
  zone?: number;
  roi?: number;
  sd?: number;
  codAmount?: number;
  codPercent?: number;
}
interface AdditionalRow extends SlabRow { sameAsAbove?: boolean }

interface B2cConfig {
  forwardBasic: SlabRow;
  forwardAdditional: AdditionalRow;
  rtoBasic: SlabRow & { sameAsForward?: boolean };
  rtoAdditional: AdditionalRow;
  // DTO = reverse pickup, collected from the customer on the merchant's instruction and returned on
  // a new waybill. A separate rate from RTO on purpose: RTO is the same consignment coming back
  // after delivery failed, a reverse pickup is a fresh leg the merchant asked for.
  dtoBasic: SlabRow & { sameAsForward?: boolean };
  dtoAdditional: AdditionalRow;
}

interface Props {
  merchantId: number;
  planId?: number;
  defaultValues?: any;
  onSuccess: () => void;
}

const blank: B2cConfig = {
  forwardBasic: {},
  forwardAdditional: {},
  rtoBasic: {},
  rtoAdditional: {},
  // Blank, including on every plan already saved: an unpriced reverse pickup bills nothing, which
  // beats quietly charging the forward rate for a service nobody quoted.
  dtoBasic: {},
  dtoAdditional: {},
};

export function B2cPricingForm({ merchantId, planId, defaultValues, onSuccess }: Props) {
  const qc = useQueryClient();
  const isEdit = !!planId;
  const [courierIds, setCourierIds] = useState<number[]>(defaultValues?.courierIds ?? []);
  const [fuel, setFuel] = useState<string>(defaultValues?.fuelChargePercent?.toString() ?? '');
  const [cfg, setCfg] = useState<B2cConfig>({ ...blank, ...(defaultValues?.config ?? {}) });
  const [pickerOpen, setPickerOpen] = useState(false);
  // Which billing plan these numbers came from.
  const [slabId, setSlabId] = useState<number | null>(defaultValues?.slabId ?? null);

  // Show B2C couriers — prefer merchant-assigned, fall back to all B2C if none assigned
  const { data: assignedMcs } = useQuery({
    queryKey: ['merchant-couriers-b2c', merchantId],
    queryFn: async () => {
      const mcs = (await api.get<any[]>(`/merchants/${merchantId}/couriers`)).data;
      return (mcs ?? []).filter((mc: any) => mc.isActive && mc.courier?.orderType === 'B2C').map((mc: any) => mc.courier as Courier);
    },
  });
  const { data: allB2cCouriers } = useQuery({
    queryKey: ['couriers', 'B2C', 'active'],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { orderType: 'B2C', isActive: 'true' } })).data,
  });
  // De-duplicate the courier list by id so the picker never shows the same courier twice.
  const couriersRaw = (assignedMcs && assignedMcs.length > 0) ? assignedMcs : (allB2cCouriers ?? []);
  const couriers = Array.from(new Map(couriersRaw.map(c => [c.id, c])).values());

  // mirror sameAs flags
  useEffect(() => {
    if (cfg.forwardAdditional.sameAsAbove) {
      setCfg(c => ({ ...c, forwardAdditional: { ...c.forwardBasic, sameAsAbove: true } as AdditionalRow }));
    }
  }, [cfg.forwardAdditional.sameAsAbove, cfg.forwardBasic]);

  useEffect(() => {
    if (cfg.rtoBasic.sameAsForward) {
      setCfg(c => ({ ...c, rtoBasic: { ...c.forwardBasic, sameAsForward: true } as any }));
    }
  }, [cfg.rtoBasic.sameAsForward, cfg.forwardBasic]);

  useEffect(() => {
    if (cfg.rtoAdditional.sameAsAbove) {
      setCfg(c => ({ ...c, rtoAdditional: { ...c.rtoBasic, sameAsAbove: true } as AdditionalRow }));
    }
  }, [cfg.rtoAdditional.sameAsAbove, cfg.rtoBasic]);

  useEffect(() => {
    if (cfg.dtoBasic?.sameAsForward) {
      setCfg(c => ({ ...c, dtoBasic: { ...c.forwardBasic, sameAsForward: true } as any }));
    }
  }, [cfg.dtoBasic?.sameAsForward, cfg.forwardBasic]);

  useEffect(() => {
    if (cfg.dtoAdditional?.sameAsAbove) {
      setCfg(c => ({ ...c, dtoAdditional: { ...c.dtoBasic, sameAsAbove: true } as AdditionalRow }));
    }
  }, [cfg.dtoAdditional?.sameAsAbove, cfg.dtoBasic]);

  const save = useMutation({
    mutationFn: async () => {
      const uniqueCourierIds = Array.from(new Set(courierIds));
      if (!anyPriceOver1(cfg)) throw new Error('Please enter at least one price greater than ₹1 before saving.');
      const payload = {
        serviceType: 'B2C',
        courierIds: uniqueCourierIds,
        fuelChargePercent: fuel ? Number(fuel) : null,
        config: cfg,
        isActive: true,
        // Kept so the plan is remembered.
        slabId,
      };
      if (isEdit) return (await api.patch(`/merchants/${merchantId}/pricing/${planId}`, payload)).data;
      return (await api.post(`/merchants/${merchantId}/pricing`, payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-pricing', merchantId] }); toast.success(isEdit ? 'Pricing updated' : 'Pricing created'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Save failed'),
  });

  const courierById = new Map((couriers ?? []).map(c => [c.id, c]));

  const togglePick = (id: number) => setCourierIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const update = <K extends keyof B2cConfig>(section: K, patch: Partial<B2cConfig[K]>) =>
    setCfg(c => ({ ...c, [section]: { ...(c[section] as any), ...patch } }));

  return (
    <div className="space-y-4">
      {/* Select Service Type pill (fixed B2C for this form) */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-slate-600 mb-1">Select Service Type</label>
          <div className="input flex items-center gap-2">
            <Chip label="B2C" />
            <span className="text-xs text-slate-400">B2C plan</span>
          </div>
        </div>
        <div className="flex-1 relative">
          <label className="block text-xs font-medium text-slate-600 mb-1">Select Couriers</label>
          <div className="input flex items-center gap-1 flex-wrap min-h-[42px] cursor-pointer"
            onClick={() => setPickerOpen(o => !o)}>
            {courierIds.length === 0
              ? <span className="text-slate-400 text-sm">Choose Couriers</span>
              : courierIds.map(id => {
                  const c = courierById.get(id);
                  return (
                    <Chip key={id} label={`${id} - ${c?.name ?? `#${id}`}`}
                      onRemove={(e) => { e?.stopPropagation(); togglePick(id); }} />
                  );
                })}
          </div>
          {pickerOpen && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {(couriers ?? []).map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={courierIds.includes(c.id)} onChange={() => togglePick(c.id)} className="rounded" />
                  <span>{c.id} - {c.name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{c.serviceType}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <PlanPicker
        serviceType="B2C"
        courierId={courierIds[0]}
        slabId={slabId}
        onPickPlan={({ slabId: id, config, fuelChargePercent }) => {
          setSlabId(id);
          setCfg({ ...blank, ...(config ?? {}) });
          if (fuelChargePercent != null) setFuel(String(fuelChargePercent));
        }}
        onCustom={() => setSlabId(null)}
      />

      <input type="number" step="0.01" placeholder="Enter fuel charge (%)" value={fuel} onChange={(e) => setFuel(e.target.value)} className="input" />

      <SlabBlock title="Forward - Basic" row={cfg.forwardBasic} section="forwardBasic"
        onChange={(p) => { update('forwardBasic', p); setSlabId(null); }} />

      <SlabBlock title="Forward - Additional" row={cfg.forwardAdditional} showSameAs="above" section="forwardAdditional"
        onChange={(p) => { update('forwardAdditional', p); setSlabId(null); }} />

      <SlabBlock title="RTO - Basic" row={cfg.rtoBasic} showSameAs="forward" section="rtoBasic"
        onChange={(p) => { update('rtoBasic', p); setSlabId(null); }} />

      <SlabBlock title="RTO - Additional" row={cfg.rtoAdditional} showSameAs="above" section="rtoAdditional"
        onChange={(p) => { update('rtoAdditional', p); setSlabId(null); }} />

      <SlabBlock title="DTO (Reverse Pickup) - Basic" row={cfg.dtoBasic ?? {}} showSameAs="forward" section="dtoBasic"
        onChange={(p) => { update('dtoBasic', p); setSlabId(null); }} />

      <SlabBlock title="DTO (Reverse Pickup) - Additional" row={cfg.dtoAdditional ?? {}} showSameAs="above" section="dtoAdditional"
        onChange={(p) => { update('dtoAdditional', p); setSlabId(null); }} />

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button type="button" onClick={onSuccess} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending || courierIds.length === 0}
          className="px-4 py-2 bg-emerald-700 text-white rounded-lg font-medium hover:bg-emerald-800 transition disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={16} className="animate-spin" />}
          SUBMIT
        </button>
      </div>
    </div>
  );
}

function SlabBlock({
  title, row, onChange, showSameAs,
}: {
  title: string;
  row: any;
  onChange: (patch: any) => void;
  showSameAs?: 'above' | 'forward';
  section?: string;
}) {
  const sameKey = showSameAs === 'forward' ? 'sameAsForward' : 'sameAsAbove';
  const sameLabel = showSameAs === 'forward' ? 'Same as Forward' : 'Same as Above';
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
      <h4 className="text-sm font-semibold text-slate-900 mb-3">{title}</h4>
      <div className="grid grid-cols-7 gap-2">
        <NumInput label="Upto (kg)" value={row.upto} onChange={(v) => onChange({ upto: v })} placeholder="Upto" select />
        <NumInput label="City (₹)" value={row.city} onChange={(v) => onChange({ city: v })} placeholder="CITY" />
        <NumInput label="State (₹)" value={row.state} onChange={(v) => onChange({ state: v })} placeholder="STATE" />
        <NumInput label="Metro (₹)" value={row.metro} onChange={(v) => onChange({ metro: v })} placeholder="METRO" />
        <NumInput label="Zone (₹)" value={row.zone} onChange={(v) => onChange({ zone: v })} placeholder="ZONE" />
        <NumInput label="ROI (₹)" value={row.roi} onChange={(v) => onChange({ roi: v })} placeholder="ROI" />
        <NumInput label="SD (₹)" value={row.sd} onChange={(v) => onChange({ sd: v })} placeholder="SD" />
        {!showSameAs && (
          <>
            <NumInput label="COD Amount (₹)" value={row.codAmount} onChange={(v) => onChange({ codAmount: v })} placeholder="COD Amount" />
            <NumInput label="COD Percent (%)" value={row.codPercent} onChange={(v) => onChange({ codPercent: v })} placeholder="COD Percent" />
          </>
        )}
        {showSameAs && (
          <label className="flex items-center gap-2 text-sm text-slate-700 col-span-2 self-end pb-2">
            <input type="checkbox" checked={!!row[sameKey]} onChange={(e) => onChange({ [sameKey]: e.target.checked })} className="rounded" />
            {sameLabel}
          </label>
        )}
      </div>
    </div>
  );
}

function NumInput({ label, value, onChange, placeholder, select }: {
  label: string; value: any; onChange: (v: any) => void; placeholder: string; select?: boolean;
}) {
  const control = select ? (
    <select value={value ?? 'Upto'} onChange={(e) => onChange(e.target.value)} className="input text-sm">
      <option>Upto</option>
      <option>0.5</option><option>1</option><option>2</option><option>5</option><option>10</option><option>20</option>
    </select>
  ) : (
    <input type="number" step="0.01" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      placeholder={placeholder} className="input text-sm" />
  );
  // Persistent label above each box so the field is identifiable even after a value is typed
  // (previously the only cue was a placeholder, which vanished on input).
  return (
    <div className="min-w-0">
      <label className="block text-[10px] font-medium text-slate-500 mb-0.5 truncate" title={label}>{label}</label>
      {control}
    </div>
  );
}

// True if the config JSON carries at least one price value greater than ₹1 anywhere.
// Mirrors the backend rule so we can give instant feedback before saving.
function anyPriceOver1(obj: any): boolean {
  if (obj == null) return false;
  if (typeof obj === 'number') return obj > 1;
  if (typeof obj === 'string') { const n = Number(obj); return Number.isFinite(n) && n > 1; }
  if (Array.isArray(obj)) return obj.some(anyPriceOver1);
  if (typeof obj === 'object') return Object.values(obj).some(anyPriceOver1);
  return false;
}

function Chip({ label, onRemove }: { label: string; onRemove?: (e?: React.MouseEvent) => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
      {label}
      {onRemove && <button type="button" onClick={onRemove} className="hover:text-danger"><XIcon size={12} /></button>}
    </span>
  );
}
